/* Reviews page bento: filters, sort and pages for the server-rendered review
   list (sections/reviews-page-bento.liquid). Every review is already in the
   HTML, newest first; this element only reorders the <li>s and hides the ones
   filtered out or off the current page. Without JavaScript the sort, the topic
   chips and the pages stay `hidden` and every review is shown.

   Filters: a star bar (`data-filter-rating`) keeps one rating, pressed again it
   clears; a topic chip (`data-filter-topic`, block id matched on the server
   into each review's `data-topics`) keeps one topic, "All" clears both. The
   two combine. Chip counts are counted here; a topic with no match is hidden.

   Strings come from the section's <template data-strings> JSON; the
   placeholders `[from]`, `[to]`, `[total]` and `[number]` are filled here. */
if (!customElements.get('lt-reviews-list')) {
  class LtReviewsList extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-list]');
      this.listCard = this.querySelector('[data-list-card]') || this;
      this.items = [...this.querySelectorAll('[data-review]')];
      this.range = this.querySelector('[data-range]');
      this.pages = this.querySelector('[data-pages]');
      this.sort = this.querySelector('[data-sort]');
      this.ratingButtons = [...this.querySelectorAll('[data-filter-rating]')];
      this.topicButtons = [...this.querySelectorAll('[data-filter-topic]')];
      this.perPage = parseInt(this.dataset.perPage, 10) || 24;
      this.rating = '';
      this.topic = '';
      this.page = 1;

      try {
        this.strings = JSON.parse(this.querySelector('template[data-strings]').innerHTML);
      } catch (error) {
        this.strings = null;
      }
      if (!this.list || !this.strings || this.items.length === 0) return;

      this.onSort = () => {
        this.applySort(this.sort.value);
        this.show(1, false);
      };
      this.onClick = (event) => {
        const page = event.target.closest('[data-page]');
        if (page && !page.disabled) {
          this.show(parseInt(page.dataset.page, 10), true);
          return;
        }
        const rating = event.target.closest('[data-filter-rating]');
        if (rating && !rating.disabled) {
          this.rating = this.rating === rating.dataset.filterRating ? '' : rating.dataset.filterRating;
          this.applyFilters();
          return;
        }
        const topic = event.target.closest('[data-filter-topic]');
        if (topic) {
          this.topic = topic.dataset.filterTopic;
          if (!this.topic) this.rating = '';
          this.applyFilters();
        }
      };

      if (this.items.length > 1) {
        this.querySelector('[data-sort-wrap]').hidden = false;
        this.sort.addEventListener('change', this.onSort);
      }
      this.countTopics();
      this.addEventListener('click', this.onClick);
      this.applyFilters();
    }

    disconnectedCallback() {
      if (this.sort && this.onSort) this.sort.removeEventListener('change', this.onSort);
      if (this.onClick) this.removeEventListener('click', this.onClick);
    }

    hasTopic(item, topic) {
      return (item.dataset.topics || '').split(' ').includes(topic);
    }

    countTopics() {
      let shown = 0;
      this.topicButtons.forEach((button) => {
        const topic = button.dataset.filterTopic;
        if (!topic) return;
        const count = this.items.filter((item) => this.hasTopic(item, topic)).length;
        const badge = button.querySelector('[data-topic-count]');
        if (badge) badge.textContent = count;
        button.hidden = count === 0;
        if (count > 0) shown += 1;
      });
      const wrap = this.querySelector('[data-topics]');
      if (wrap) wrap.hidden = shown === 0;
    }

    applyFilters() {
      this.ratingButtons.forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.filterRating === this.rating ? 'true' : 'false');
      });
      const bars = this.ratingButtons[0] && this.ratingButtons[0].closest('ul');
      if (bars) bars.classList.toggle('is-filtered', this.rating !== '');

      this.topicButtons.forEach((button) => {
        const current = button.dataset.filterTopic === this.topic;
        button.setAttribute('aria-pressed', current ? 'true' : 'false');
        button.classList.toggle('m-button--primary', current);
        button.classList.toggle('m-button--secondary', !current);
      });

      this.filtered = this.items.filter(
        (item) =>
          (!this.rating || String(parseInt(item.dataset.rating, 10)) === this.rating) &&
          (!this.topic || this.hasTopic(item, this.topic))
      );
      this.show(1, false);
    }

    applySort(mode) {
      const order = (item) => parseInt(item.dataset.order, 10);
      const rating = (item) => parseFloat(item.dataset.rating) || 0;
      const compare = {
        newest: (a, b) => order(a) - order(b),
        oldest: (a, b) => order(b) - order(a),
        highest: (a, b) => rating(b) - rating(a) || order(a) - order(b),
        lowest: (a, b) => rating(a) - rating(b) || order(a) - order(b)
      }[mode] || ((a, b) => order(a) - order(b));

      this.items.sort(compare);
      this.filtered.sort(compare);
      const fragment = document.createDocumentFragment();
      this.items.forEach((item) => fragment.appendChild(item));
      this.list.appendChild(fragment);
    }

    show(page, scroll) {
      const total = this.filtered.length;
      const pageCount = Math.max(1, Math.ceil(total / this.perPage));
      this.page = Math.min(Math.max(page, 1), pageCount);
      const start = (this.page - 1) * this.perPage;
      const end = Math.min(start + this.perPage, total);
      const visible = new Set(this.filtered.slice(start, end));

      this.items.forEach((item) => {
        item.hidden = !visible.has(item);
      });

      this.range.textContent = total === 0
        ? this.strings.empty
        : this.strings.range.replace('[from]', start + 1).replace('[to]', end).replace('[total]', total);

      this.renderPages(pageCount);

      if (scroll) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.listCard.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }

    renderPages(pageCount) {
      this.pages.hidden = pageCount < 2;
      if (pageCount < 2) {
        this.pages.innerHTML = '';
        return;
      }

      const button = (target, label, text, current, disabled) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = `m-button m-button--small m-bento-pill ${current ? 'm-button--primary' : 'm-button--secondary'}`;
        el.dataset.page = target;
        el.setAttribute('aria-label', label);
        el.textContent = text;
        if (current) el.setAttribute('aria-current', 'page');
        if (disabled) el.disabled = true;
        return el;
      };

      const fragment = document.createDocumentFragment();
      fragment.appendChild(button(this.page - 1, this.strings.previous, '‹', false, this.page === 1));
      for (let i = 1; i <= pageCount; i += 1) {
        fragment.appendChild(button(i, this.strings.page.replace('[number]', i), String(i), i === this.page, false));
      }
      fragment.appendChild(button(this.page + 1, this.strings.next, '›', false, this.page === pageCount));

      this.pages.innerHTML = '';
      this.pages.appendChild(fragment);
    }
  }

  customElements.define('lt-reviews-list', LtReviewsList);
}
